import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography
} from '@material-ui/core';
import { DecisionDialog, LoaderBar, Page } from 'src/components';
import { isEqual } from 'lodash';
import { useFormik } from 'formik';
import { usePermissions, useSnackBar } from 'src/hooks';
import { useParams } from 'react-router';
// import usePrompt from 'src/utils/navigation-prompt';
import { ChequeForm } from './components/ChequeForm';
import {
  UpdateChequePayload,
  UpdateChequeApiPayload,
  PurchaseOrderItem
} from 'src/redux/slices/cheque-management';
import { useCheque } from 'src/hooks/cheque-management/use-cheque';
import { Alert } from '@material-ui/lab';
import EditIcon from '@material-ui/icons/Edit';
import NoteAddIcon from '@material-ui/icons/NoteAdd';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  }
}));

export const component = () => {
  const { id } = useParams();
  const classes = useStyles();
  const snackbar = useSnackBar();

  const { canUpdatePurchaseOrder, canDeletePurchaseOrder } = usePermissions();

  const {
    isLoading,
    updateCheque,
    fetchChequeDetails,
    chequeDetails
  } = useCheque();

  const { deleteCheque } = useCheque();
  const [isDeltetCheque, setIsDeleteCheque] = useState<boolean>(false);
  const [isOpenInternalRemarks, setOpenInternalRemarks] = useState<boolean>(
    false
  );
  const [reasonText, setReasonText] = useState<string>('');

  const onClickUpdate = async (values: UpdateChequePayload) => {
    if (isEqual(values, chequeDetails)) {
      snackbar.show({
        message: `No changes detected`,
        severity: 'error',
        useSound: true
      });
      return;
    }

    // eslint-disable-next-line no-undef
    const newChequeDetails: Partial<UpdateChequeApiPayload> = {
      cheque_id: Number(id)
    };

    if (values.cheque_no !== chequeDetails?.cheque_no) {
      newChequeDetails.cheque_no = values.cheque_no;
    }
    if (values.dr_no !== chequeDetails?.dr_no) {
      newChequeDetails.dr_no = values.dr_no;
    }
    if (values.pdc_date !== chequeDetails?.pdc_date) {
      newChequeDetails.pdc_date = values.pdc_date;
    }
    if (values.liability !== chequeDetails?.liability) {
      newChequeDetails.liability = values.liability;
    }
    if (!isEqual(values.po_nos, chequeDetails?.po_nos)) {
      newChequeDetails.po_nos = values?.po_nos?.map((po) => po.po_no);
      newChequeDetails.supplier_id = values.supplier_id;
    }
    if (values.amount !== chequeDetails?.amount) {
      newChequeDetails.amount = values.amount;
    }
    if (values.cheque_img_url !== chequeDetails?.cheque_img_url) {
      newChequeDetails.cheque_img_url = values.cheque_img_url;
    }
    if (values.supplier_id !== chequeDetails?.supplier_id) {
      newChequeDetails.supplier_id = values.supplier_id;
    }

    if (values.credit_memo_amount !== chequeDetails?.credit_memo_amount) {
      newChequeDetails.credit_memo_amount = values.credit_memo_amount;
    }

    await updateCheque(newChequeDetails, true, () => {
      snackbar.show({
        message: 'Cheque Details Updated',
        severity: 'success'
      });
      fetchPurchaseOrderDetails();
    });
  };

  const fetchPurchaseOrderDetails = async () => {
    await fetchChequeDetails(Number(id));

    if (!isLoading) {
      chequeDetails ? formikRef.setValues(chequeDetails ?? {}, false) : false;
    }
  };

  const formSchema = Yup.object().shape({
    cheque_no: Yup.string().required('Cheque No. is required'),
    pdc_date: Yup.date().required('PDC Date is required'),
    amount: Yup.number().required('Amount is required'),
    supplier_id: Yup.number().required('Supplier is required')
  });

  // ts band-aid
  const formikRef: any = useFormik({
    initialValues: chequeDetails,
    enableReinitialize: true,
    onSubmit: canUpdatePurchaseOrder ? onClickUpdate : () => {},
    validationSchema: formSchema,
    validateOnChange: true
  });

  const grandTotal = useMemo(() => {
    const grand_total = (formikRef?.values?.po_nos || []).reduce(
      (accumulator: number, currentPo: PurchaseOrderItem) =>
        accumulator + (currentPo?.total_amount || currentPo?.amount || 0),
      0
    );

    return Math.round((grand_total + Number.EPSILON) * 100) / 100;
  }, [formikRef]);

  const hasPo = !!formikRef?.values?.po_nos?.[0];

  const handleSubmitWithValidation = async () => {
    const errors = await formikRef.validateForm();
    const errorsArr = Object.entries(errors) ?? [];
    const isGrandTotalAmountValid =
      Math.abs(
        Number(formikRef?.values?.amount || 0) +
          Number(formikRef?.values?.credit_memo_amount || 0) -
          Number(grandTotal || 0)
      ) < 0.01;

    if (!isGrandTotalAmountValid) {
      snackbar?.show({
        severity: 'error',
        message: 'The amount and credit memo total must match the grand total.',
        useSound: true
      });
      return;
    }

    if (errorsArr.length > 0) {
      snackbar.show({
        message: `${errorsArr[0][1]}`,
        severity: 'error'
      });
    } else {
      formikRef.handleSubmit();
    }
  };

  const onHandleDeleteCheque = (cheque_id: number) => {
    if (
      canDeletePurchaseOrder &&
      chequeDetails?.po_nos &&
      chequeDetails?.po_nos?.length <= 0
    ) {
      deleteCheque(cheque_id);
    }
  };

  const onUpdateInternalRemarks = async (id?: number, reason?: string) => {
    await updateCheque(
      { cheque_id: id, internal_remarks: reason },
      true,
      () => {
        snackbar.show({
          message: 'Cheque Details Updated',
          severity: 'success'
        });
        fetchPurchaseOrderDetails();
      }
    );
  };

  useEffect(() => {
    fetchPurchaseOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => setReasonText(chequeDetails?.internal_remarks || ''), [
    chequeDetails.internal_remarks
  ]);

  //TODO:will change later
  // usePrompt(
  //   'There are some unsaved changes. Are you sure you want to leave?',
  //   formikRef.dirty // true if has changes
  // );

  return (
    <Page
      className={classes.root}
      title={`${formikRef.values?.cheque_no || 'Purchase Order'}`}
    >
      <Container maxWidth={false}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item md={6} sm={6} xs={12}>
            <Grid container alignItems="center" spacing={2}>
              <Typography color="textPrimary" gutterBottom variant="h3">
                Cheque Details
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        {chequeDetails?.internal_remarks ? null : (
          <Box
            mt={3}
            mb={1}
            sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenInternalRemarks(true)}
              startIcon={<NoteAddIcon />}
            >
              Add Remarks
            </Button>
          </Box>
        )}
        <LoaderBar isLoading={isLoading} />
        <Box mt={4} style={{ opacity: isLoading ? 0.3 : 1 }}>
          <ChequeForm
            grandTotal={grandTotal}
            formikRef={formikRef}
            isEditable={canUpdatePurchaseOrder}
          />
        </Box>

        <Grid
          container
          style={{
            justifyContent: 'space-between',
            rowGap: '1em',
            marginTop: '1em'
          }}
        >
          <Grid item xs={12} sm={2}>
            {canDeletePurchaseOrder ? (
              <>
                <Tooltip
                  title={
                    hasPo
                      ? 'Remove all Purchase Orders associated to delete this check'
                      : ''
                  }
                  disableHoverListener={!hasPo}
                >
                  <span>
                    <Button
                      style={{ height: '100%' }}
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={() => setIsDeleteCheque(true)}
                      disabled={
                        !canDeletePurchaseOrder ||
                        (chequeDetails?.po_nos &&
                          chequeDetails?.po_nos.length > 0)
                      }
                    >
                      Delete
                    </Button>
                  </span>
                </Tooltip>
              </>
            ) : null}
          </Grid>

          <Grid item xs={12} sm={2}>
            {canUpdatePurchaseOrder ? (
              <Button
                fullWidth
                color="primary"
                size="large"
                variant="contained"
                type="button"
                onClick={handleSubmitWithValidation}
              >
                Update
              </Button>
            ) : null}
          </Grid>
        </Grid>

        {/* Old layout retain for easy revert */}
        {/* <Box
          mb={2}
          mt={4}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1em'
          }}
        >
          {canDeletePurchaseOrder ? (
            <>
              <Tooltip
                title={
                  hasPo
                    ? 'Remove all Purchase Orders associated to delete this check'
                    : ''
                }
                disableHoverListener={!hasPo}
              >
                <span>
                  <Button
                    style={{ width: '20em', height: '100%' }}
                    color="secondary"
                    variant="outlined"
                    onClick={() => setIsDeleteCheque(true)}
                    disabled={!canDeletePurchaseOrder || hasPo}
                  >
                    Delete
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : null}

          {canUpdatePurchaseOrder ? (
            <Button
              style={{ width: '20em' }}
              color="primary"
              size="large"
              variant="contained"
              type="button"
              onClick={handleSubmitWithValidation}
            >
              Update
            </Button>
          ) : null}
        </Box> */}

        <Box
          mt={3}
          mb={1}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%'
          }}
        >
          {chequeDetails?.internal_remarks ? (
            <Alert
              style={{ width: '100%' }}
              severity="info"
              action={
                <IconButton
                  aria-label="edit"
                  size="small"
                  onClick={() => setOpenInternalRemarks(true)}
                >
                  <EditIcon fontSize="medium" />
                </IconButton>
              }
            >
              <Typography variant="h5" style={{ whiteSpace: 'pre-line' }}>
                {chequeDetails?.internal_remarks || ''}
              </Typography>
            </Alert>
          ) : null}
        </Box>
        <DecisionDialog
          isOpen={isDeltetCheque}
          title={`Delete Cheque ${chequeDetails?.cheque_no || ''}`}
          subTitle={`Are you sure you want to delete this cheque? This action cannot be undone.`}
          onHandleConfirmAction={() =>
            onHandleDeleteCheque(chequeDetails?.cheque_id || 0)
          }
          onHandleClose={() => setIsDeleteCheque(false)}
        />
      </Container>
      <DecisionDialog
        isOpen={isOpenInternalRemarks}
        title="Add cheque detail remarks"
        subTitle="Add informative notes to cheque details"
        reasonValue={reasonText}
        showReasonBox={true}
        okayButtonName="Add Remarks"
        onHandleConfirmAction={() =>
          onUpdateInternalRemarks(
            chequeDetails?.cheque_id || undefined,
            reasonText
          )
        }
        onReasonChange={(text?: string) => setReasonText(text || '')}
        onHandleClose={() => setOpenInternalRemarks(false)}
      />
    </Page>
  );
};

export const ChequeDetailsView = React.memo(component);
